import { Capacitor } from '@capacitor/core';
import { clearTenantPayRoomCode } from './tenantPayRoom';

export const MOBILE_APP_LOGIN_PATH = '/mobile/login';
export const MOBILE_APP_HOME_PATH = '/mobile/app';

const TENANT_MOBILE_SESSION_KEY = 'kcn_tenant_mobile_session';
const OPEN_TENANT_QR_FLAG = 'kcn_open_tenant_qr';

/** Chủ nhà: chỉ trong phiên app hiện tại. Khách thuê QR: lưu localStorage — tắt app vẫn vào lại được. */
let sessionUnlockedThisProcess = false;

function hasAuthTokenInStorage(): boolean {
  try {
    return Boolean(localStorage.getItem('auth_token') || localStorage.getItem('token'));
  } catch {
    return false;
  }
}

function bootstrapPersistedTenantSession(): void {
  // Không tự unlock — khách thuê bấm "Tiếp tục" hoặc đăng nhập CCCD; chủ nhà luôn qua màn login.
}

export function isMobileSessionUnlocked(): boolean {
  return sessionUnlockedThisProcess;
}

/** Gọi sau khi đăng nhập mật khẩu / vân tay (chủ nhà) thành công. */
export function unlockMobileSession(): void {
  sessionUnlockedThisProcess = true;
}

export function lockMobileSession(): void {
  sessionUnlockedThisProcess = false;
}

/** Khách thuê: ghi nhớ đã đăng nhập CCCD — lần sau mở app không cần nhập lại. */
export function persistTenantMobileSession(userId: string | number): void {
  try {
    localStorage.setItem(TENANT_MOBILE_SESSION_KEY, '1');
    localStorage.setItem(`${TENANT_MOBILE_SESSION_KEY}_uid`, String(userId));
    unlockMobileSession();
  } catch {
    unlockMobileSession();
  }
}

export function hasPersistedTenantMobileSession(): boolean {
  try {
    return localStorage.getItem(TENANT_MOBILE_SESSION_KEY) === '1' && hasAuthTokenInStorage();
  } catch {
    return false;
  }
}

export function clearPersistedTenantMobileSession(): void {
  try {
    localStorage.removeItem(TENANT_MOBILE_SESSION_KEY);
    localStorage.removeItem(`${TENANT_MOBILE_SESSION_KEY}_uid`);
  } catch {
    // ignore
  }
  lockMobileSession();
}

/** Đăng xuất khách thuê — xóa phiên lưu + mã phòng, lần sau bắt buộc nhập CCCD. */
export function clearTenantMobileLoginData(userId?: string | null): void {
  clearPersistedTenantMobileSession();
  clearTenantPayRoomCode(userId ?? undefined);
}

/** Chỉ mở form QR khách thuê khi phiên tenant hết hạn — không tự mở khi chủ nhà đăng nhập. */
export function requestTenantQrLoginScreen(): void {
  try {
    sessionStorage.setItem(OPEN_TENANT_QR_FLAG, '1');
  } catch {
    // ignore
  }
}

export function consumeTenantQrLoginRequest(): boolean {
  try {
    if (sessionStorage.getItem(OPEN_TENANT_QR_FLAG) !== '1') return false;
    sessionStorage.removeItem(OPEN_TENANT_QR_FLAG);
    return true;
  } catch {
    return false;
  }
}

const LAST_MOBILE_TAB_KEY = 'kcn_mobile_last_tab';

const NATIVE_FEATURE_PREFIXES = [
  '/add-product',
  '/edit-product',
  '/contracts',
  '/upgrade',
  '/auth/kyc',
];

/** Chạy trong APK Android/iOS (Capacitor native shell). */
export function isNativeMobileApp(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

export function isNativeFeaturePath(pathname: string): boolean {
  return NATIVE_FEATURE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export function setLastMobileTab(tab: string): void {
  try {
    sessionStorage.setItem(LAST_MOBILE_TAB_KEY, tab);
  } catch {
    // ignore
  }
}

export function getLastMobileTab(): string | null {
  try {
    return sessionStorage.getItem(LAST_MOBILE_TAB_KEY);
  } catch {
    return null;
  }
}

/** Sau thêm/sửa phòng hoặc đóng trang phụ — quay lại shell app. */
export function getNativeReturnPath(webPath: string): string {
  return isNativeMobileApp() ? MOBILE_APP_HOME_PATH : webPath;
}

bootstrapPersistedTenantSession();

/** Đường dẫn đăng nhập phù hợp (web hoặc app). */
export function getAppLoginPath(): string {
  return isNativeMobileApp() ? MOBILE_APP_LOGIN_PATH : '/auth/login';
}

/** Sau đăng nhập thành công — vào shell app hoặc profile web. */
export function getPostLoginPath(preferredFrom?: string | null): string {
  if (preferredFrom && preferredFrom.startsWith('/mobile') && preferredFrom !== MOBILE_APP_LOGIN_PATH) {
    return preferredFrom;
  }
  if (isNativeMobileApp()) {
    return MOBILE_APP_HOME_PATH;
  }
  return preferredFrom && preferredFrom !== '/auth/login' && preferredFrom !== '/auth/register'
    ? preferredFrom
    : '/profile';
}
